import { LAST_5_YEARS } from '@modules/common/constants';
import { useFetchRecordCount } from '@modules/common/hooks';
import { formatNumToCompactNotation } from '@modules/common/utils';
import { MetadataFieldMappingFeedbackDrawer } from '@modules/integrationsV2/components/LinkIntegrationMetadata/MetadataFieldMappingList/MetadataFieldMappingFeedbackDrawer';
import { MetadataFieldMappingMatchCountSkeleton } from '@modules/integrationsV2/components/LinkIntegrationMetadata/MetadataFieldMappingList/MetadataFieldMappingMatchCountSkeleton';
import {
  getWhereClauseForMetadataFieldMapping,
  trackIntegrationPageAddLinkEvent,
} from '@modules/integrationsV2/utils';
import { InfoOutlined } from '@mui/icons-material';
import { Box, Link, Tooltip } from '@mui/material';
import { EqlField } from 'generated/graphql';
import { useMemo, useState } from 'react';
import { AggregatePeriod, TimeRange } from 'v2/core/entities';
import { metadataWhereClauseGenerator } from 'v2/core/services/queryBuilder/whereClauseGenerator';

interface MetadataFieldMappingFeedbackMatchCountProps {
  sourceEqlField: EqlField;
  targetEqlField: EqlField;
}

export const MetadataFieldMappingFeedbackMatchCount = ({
  sourceEqlField,
  targetEqlField,
}: MetadataFieldMappingFeedbackMatchCountProps) => {
  const [isFeedbackDrawerOpen, setIsFeedbackDrawerOpen] = useState(false);

  const queryContainerForMatches = useMemo(
    () => ({
      DateRange: LAST_5_YEARS as TimeRange,
      WhereClause: getWhereClauseForMetadataFieldMapping({
        sourceField: sourceEqlField.field,
        targetField: targetEqlField.field,
      }),
      AggregatePeriod: AggregatePeriod.MONTH,
    }),
    [sourceEqlField.field, targetEqlField.field],
  );

  const queryContainerForSource = useMemo(
    () => ({
      DateRange: LAST_5_YEARS as TimeRange,
      WhereClause: metadataWhereClauseGenerator({
        type: 'string',
        values: [sourceEqlField.field],
      }),
      AggregatePeriod: AggregatePeriod.MONTH,
    }),
    [sourceEqlField.field],
  );

  const {
    data: feedbackCountOfSource,
    isLoading: isFetchingFeedbackCountOfSource,
  } = useFetchRecordCount({
    queryContainer: queryContainerForSource,
  });

  const {
    data: feedbackCountOfMatches,
    isLoading: isFetchingFeedbackCountOfMatches,
  } = useFetchRecordCount({
    queryContainer: queryContainerForMatches,
  });

  const handleClick = () => {
    setIsFeedbackDrawerOpen(true);
    trackIntegrationPageAddLinkEvent({
      eventName: 'Link Integrations - Feedback Matches Drawer Opened',
      targetIntegrationField: {
        displayName: targetEqlField.displayName,
        objectType: targetEqlField.objectType,
      },
      sourceIntegrationField: {
        displayName: sourceEqlField.displayName,
        objectType: sourceEqlField.objectType,
      },
    });
  };

  const percentageOfMatches =
    feedbackCountOfSource &&
    feedbackCountOfSource !== 0 &&
    feedbackCountOfMatches
      ? Math.round(
          (feedbackCountOfMatches / feedbackCountOfSource) * 100 * 100,
        ) / 100
      : 0;

  const getMatchesInfoTooltipContent = () => {
    if (!feedbackCountOfSource || !percentageOfMatches) {
      return null;
    }

    return `${percentageOfMatches}% of ${formatNumToCompactNotation({
      value: feedbackCountOfSource ?? 0,
      formatSmallerNumToStandardNotation: false,
      maximumFractionDigits: 1,
    })} feedback records from ${sourceEqlField.displayName}`;
  };

  return (
    <>
      <Link
        onClick={handleClick}
        component="button"
        variant="caption"
        width="100%"
        textAlign="center"
        lineHeight="0.9rem"
        disabled={!feedbackCountOfMatches}
        color={!feedbackCountOfMatches ? 'text.secondary' : 'primary'}
        underline={!feedbackCountOfMatches ? 'none' : 'hover'}
        sx={{
          cursor: !feedbackCountOfMatches ? 'default' : 'pointer',
          userSelect: !feedbackCountOfMatches ? 'all' : undefined,
        }}
      >
        <Box>
          {!isFetchingFeedbackCountOfMatches &&
          feedbackCountOfMatches !== undefined ? (
            formatNumToCompactNotation({
              value: feedbackCountOfMatches,
              formatSmallerNumToStandardNotation: false,
            })
          ) : (
            <Box mr={0.2} display="inline-block">
              <MetadataFieldMappingMatchCountSkeleton />
            </Box>
          )}{' '}
          matches
        </Box>
        {!isFetchingFeedbackCountOfMatches &&
        !isFetchingFeedbackCountOfSource ? (
          <Box>
            <Tooltip arrow title={getMatchesInfoTooltipContent()}>
              <InfoOutlined
                fontSize="small"
                sx={{
                  color: 'text.secondary',
                }}
              />
            </Tooltip>
          </Box>
        ) : (
          <Box mr={0.2} display="inline-block">
            <MetadataFieldMappingMatchCountSkeleton />
          </Box>
        )}
      </Link>
      {sourceEqlField && targetEqlField && (
        <MetadataFieldMappingFeedbackDrawer
          isOpen={isFeedbackDrawerOpen}
          onClose={() => {
            setIsFeedbackDrawerOpen(false);
          }}
          sourceEqlField={sourceEqlField}
          targetEqlField={targetEqlField}
          queryContainer={queryContainerForMatches}
        />
      )}
    </>
  );
};
